import { fetchAuthSession } from "aws-amplify/auth";

const API_BASE = process.env.REACT_APP_API;

interface PresignedUrlResponse {
  success: boolean;
  data: {
    uploadUrl: string;
    publicUrl: string;
  };
}

/**
 * Uploads an image blob to S3 via a presigned URL obtained from the backend.
 *
 * Flow:
 * 1. Requests a presigned PUT URL from the backend (POST /bo/uploads/presigned-url)
 * 2. PUTs the blob directly to S3 using the presigned URL
 * 3. Returns the public URL of the uploaded image
 *
 * @param blob - The image blob to upload (result of canvas crop)
 * @param folder - "covers" (1290x550), "gallery" (1290x900), "benefits" (marketplace), "banners" (1280x720) o "rewards" (catálogo de premios)
 * @param fileName - Optional custom file name, defaults to "image.jpg"
 * @returns The public S3 URL of the uploaded image
 */
export async function uploadImageToS3(
  blob: Blob,
  folder: "covers" | "gallery" | "benefits" | "banners" | "rewards",
  fileName: string = "image.jpg"
): Promise<string> {
  // Step 1: Get presigned URL from backend
  console.log("[uploadToS3] Starting upload:", { folder, fileName, blobType: blob.type, blobSize: blob.size });

  const session = await fetchAuthSession();
  const accessToken = session.tokens?.accessToken?.toString() ?? "";

  const presignedResponse = await fetch(`${API_BASE}/bo/uploads/presigned-url`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      fileName,
      contentType: blob.type || "image/jpeg",
      folder,
    }),
  });

  if (!presignedResponse.ok) {
    const errorText = await presignedResponse.text();
    console.error("[uploadToS3] Failed to get presigned URL:", presignedResponse.status, errorText);
    throw new Error(
      `Failed to get presigned URL: ${presignedResponse.status} ${presignedResponse.statusText}`
    );
  }

  const presignedData: PresignedUrlResponse = await presignedResponse.json();
  console.log("[uploadToS3] Presigned URL response:", {
    success: presignedData.success,
    uploadUrl: presignedData.data?.uploadUrl?.substring(0, 150) + "...",
    publicUrl: presignedData.data?.publicUrl,
  });

  if (!presignedData.success || !presignedData.data?.uploadUrl) {
    throw new Error("Invalid presigned URL response from server");
  }

  const { uploadUrl, publicUrl } = presignedData.data;

  // Step 2: Upload directly to S3 (no extra headers — only host is signed)
  console.log("[uploadToS3] Uploading to S3...", { method: "PUT", blobSize: blob.size, blobType: blob.type });
  const uploadResponse = await fetch(uploadUrl, {
    method: "PUT",
    body: blob,
  });

  console.log("[uploadToS3] S3 response:", {
    status: uploadResponse.status,
    statusText: uploadResponse.statusText,
    headers: Object.fromEntries(uploadResponse.headers.entries()),
  });

  if (!uploadResponse.ok) {
    const errorBody = await uploadResponse.text();
    console.error("[uploadToS3] S3 upload error body:", errorBody);
    throw new Error(
      `Failed to upload to S3: ${uploadResponse.status} ${uploadResponse.statusText}`
    );
  }

  console.log("[uploadToS3] Upload successful! Public URL:", publicUrl);
  // Step 3: Return the public URL
  return publicUrl;
}

/**
 * Checks if the natural dimensions of an image meet the minimum requirements.
 * Returns true if dimensions are >= required, false otherwise.
 */
export function checkImageDimensions(
  file: File,
  minWidth: number,
  minHeight: number
): Promise<{ valid: boolean; width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        valid: img.naturalWidth >= minWidth && img.naturalHeight >= minHeight,
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => {
      resolve({ valid: false, width: 0, height: 0 });
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Checks if an image's dimensions exactly match the target crop size.
 * If so, the cropper can be skipped and the file uploaded directly.
 */
export function checkExactDimensions(
  file: File,
  targetWidth: number,
  targetHeight: number
): Promise<{ exact: boolean; width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        exact:
          img.naturalWidth === targetWidth &&
          img.naturalHeight === targetHeight,
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => {
      resolve({ exact: false, width: 0, height: 0 });
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(file);
  });
}
